import type {
  PostMedia,
  PostPlatform,
  PostPlatformTarget,
  PostTargetInput,
} from '../../types/publishing.js';
import { resolveEffectiveContent } from './effectivePost.js';

// Publishing — per-platform composability rules (Phase 1). Pure + total. Shared by
// the composer (client-side warnings before publish) and the server (hard-blocks
// on publish). A rule is either an ERROR (blocks a real publish; drafts are always
// allowed) or a WARNING (advisory — e.g. the AM's IG feed/reel intent contradicts
// the media type, which Zernio would silently reinterpret).
//
// Grounding (see the findings note):
//   - GBP: text + SINGLE IMAGE only — no video, at most one media item.
//   - IG:  cannot post text-only (media required). 'reel' needs video; 'feed'/'story'
//          with a video → Zernio makes a Reel/story-video (warn on feed+video).
//   - FB:  text-only allowed. 'story'/'reel' + no media is invalid.
//   - firstComment: unsupported on IG/FB STORIES (and GBP has none at all).

export const PLATFORM_LABEL: Record<PostPlatform, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  googlebusiness: 'Google Business',
};

export const GBP_MAX_MEDIA = 1;

export interface PostRuleIssue {
  platform: PostPlatform;
  level: 'error' | 'warning';
  message: string;
}

function firstMediaType(media: PostMedia[]): 'image' | 'video' | null {
  return media.length > 0 ? media[0]!.type : null;
}

// Validate ONE target against the post's base content/media (resolving the
// effective caption/media for this platform first).
export function validatePostTarget(
  base: { content: string; media: PostMedia[] },
  target: PostPlatformTarget | PostTargetInput,
): PostRuleIssue[] {
  const issues: PostRuleIssue[] = [];
  const platform = target.platform;
  const eff = resolveEffectiveContent(base, target);
  const media = eff.media;
  const mediaType = firstMediaType(media);
  const hasText = eff.content.trim() !== '';
  const push = (level: 'error' | 'warning', message: string) =>
    issues.push({ platform, level, message });

  if (platform === 'googlebusiness') {
    if (media.some((m) => m.type === 'video')) {
      push('error', 'Google Business posts are text + single image only — remove the video.');
    }
    if (media.length > GBP_MAX_MEDIA) {
      push('error', `Google Business posts support one image only (you have ${media.length}).`);
    }
    if (!hasText) {
      push('error', 'Google Business posts need some text.');
    }
    const gbp = 'gbp' in target ? target.gbp : null;
    if (gbp?.topicType === 'EVENT' && !gbp.event) {
      push('error', 'Event posts need an event title and dates.');
    }
    if (gbp?.cta && (!gbp.cta.url || gbp.cta.url.trim() === '')) {
      push('error', 'The call-to-action needs a destination URL.');
    }
    return issues;
  }

  if (platform === 'instagram') {
    if (media.length === 0) {
      push('error', 'Instagram needs at least one image or video.');
      return issues;
    }
    const ig = 'igContentType' in target ? target.igContentType : null;
    if (ig === 'reel' && mediaType !== 'video') {
      push('error', 'An Instagram Reel needs a video.');
    }
    if (ig === 'feed' && mediaType === 'video') {
      push(
        'warning',
        'A video on Instagram publishes as a Reel, not a feed post — Instagram decides this from the media.',
      );
    }
    if (ig === 'story' && (target.firstComment ?? '').trim() !== '') {
      push('warning', 'Instagram Stories don’t support a first comment — it will be skipped.');
    }
    return issues;
  }

  // facebook
  if (media.length === 0 && !hasText) {
    push('error', 'Add some text or media for Facebook.');
  }
  const fb = 'fbContentType' in target ? target.fbContentType : null;
  if ((fb === 'story' || fb === 'reel') && media.length === 0) {
    push('error', `A Facebook ${fb} needs media.`);
  }
  if (fb === 'reel' && mediaType !== 'video') {
    push('error', 'A Facebook Reel needs a video.');
  }
  if (fb === 'story' && (target.firstComment ?? '').trim() !== '') {
    push('warning', 'Facebook Stories don’t support a first comment — it will be skipped.');
  }
  return issues;
}

// Validate a whole post (all targets) + cross-cutting rules (at least one target,
// each target must have some content or media). Returns every issue, errors first.
export function validatePost(post: {
  content: string;
  media: PostMedia[];
  targets: Array<PostPlatformTarget | PostTargetInput>;
}): PostRuleIssue[] {
  const issues: PostRuleIssue[] = [];
  for (const t of post.targets) {
    issues.push(...validatePostTarget({ content: post.content, media: post.media }, t));
  }
  return issues.sort((a, b) => (a.level === b.level ? 0 : a.level === 'error' ? -1 : 1));
}

export function hasBlockingIssues(issues: PostRuleIssue[]): boolean {
  return issues.some((i) => i.level === 'error');
}
